/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Constants } from '@app/constants';
import { AppNames } from '@app/models/AppNames';
import {
  eProductNames,
  eProductRateNames
} from '@app/models/fi-products.model';
import { ILoan } from '@app/models/loan.model';
import { LoanType } from '@app/models/Loan-Type';
import {
  eProductLevel,
  getDefaultProdSelectionObject,
  getNewMmpRequest,
  IRateRequest
} from '@app/models/rate-request.model';
import {
  CvpRate,
  GapRate,
  SdRate,
  getEmptyProductSelectionRateCollectionId,
  getEmptyRateCollectionRequest,
  IExistingRatesResponse,
  IRateResponse,
  KeyProtectionRate,
  MmpRate,
  PepRate,
  PlanOption,
  PpmRate,
  RateCollectionRequest,
  UProductRate,
  UProductRates,
  VehicleReturnRate
} from '@app/models/rate-response.model';
import {
  PPI_INELIGIBLE_AGE_RULE_MESSAGE
} from '@app/shared/constants/globalConstants';
import {
  filterPpiRatesByAge,
  isIgnoreError,
  loanToValue,
  PPIAgeEligibility
} from '@app/shared/utils/utility';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, concatMap, take, tap } from 'rxjs/operators';

import { NotificationService } from './notification.service';
import { UnityDirectStateService } from './unity-direct-state.service';
import { HubDataService } from './hubData.service';
import Big from 'big.js';
import { MMPDefaults } from '@app/models/MMPDefaults';

@Injectable({ providedIn: 'root' })
export class UnityDirectRatesService {
  totalAmountFinanced: Big = new Big(0);
  messages: string[] = [];
  mockVin: string;
  /**
   * Emits the mockVin returned by GetVinLookup on the Manual Build path.
   * Consumers (e.g. the valuation card) subscribe to react when the VIN
   * becomes available after a manual year/make/model/style selection.
   */
  private readonly _mockVin$ = new BehaviorSubject<string>(null);
  readonly mockVin$ = this._mockVin$.asObservable();
  penTrackKeys: any = {};
  isLoanToValueSet: boolean = false;
  mmpExclusionText: string;

  constructor(
    @Inject(UnityDirectStateService) private udStateService,
    private notificationService: NotificationService,
    @Inject(HubDataService) protected hubDataService,
    private http: HttpClient,
    private logger: NGXLogger
  ) { }

  // Set mockVin when VIN is empty
  setMockVin(vin: string): void {
    this.mockVin = vin;
    this._mockVin$.next(vin);
  }

  /**
   * Reset Pentrack keys
   */
  resetPenTrackKeys(): void {
    this.penTrackKeys = {};
  }

  /**
   * Initialize rating by invoking startRatingCallSequence and returning it's observable
   * @param skipProductsRerate {eProductNames[]} array of product names that should be skipped for rating
   * @returns Observable<any>
   */
  initRating(skipProductsRerate?: eProductNames[]): Observable<any> {
    return this.startRatingCallSequence(skipProductsRerate);
  }

  /**
   * Starts the rate call sequence depending on the product pyramid
   * @param skipProductsRerate {eProductNames[]} <optional> array of product names that should be skipped for rating
   * @returns Observable<any>
   */
  startRatingCallSequence(
    skipProductsRerate?: eProductNames[]
  ): Observable<any> {
    let currentProcessingLevel: eProductLevel = null;
    this.udStateService.isRatingInProgressSubject.next(true);
    this.calculateInitialTAF(skipProductsRerate);
    this.udStateService.totalAmountFinanced =
      this.totalAmountFinanced.toNumber();
    this.udStateService.setCurrentSelectedProductRates(true);
    const prodPyramid = this.udStateService.getProductPyramid();
    // Remove the empty levels so ConcatMap doesn't get null or undefined values.
    for (let i = 0; i < Object.keys(prodPyramid).length;) {
      const level = Object.keys(prodPyramid)[i];
      if (!prodPyramid[level].length) {
        delete prodPyramid[level];
      } else {
        i++;
      }
    }
    this.logger.info('Product Pyramid: ', prodPyramid);
    return from(Object.keys(prodPyramid) as eProductLevel[]).pipe(
      concatMap((level: eProductLevel) => {
        if (prodPyramid[level]?.length) {
          currentProcessingLevel = level;
          return this.getProductRates(level).pipe(
            tap((response: IRateResponse) => {
              if (response) {
                this.processRateResponse(currentProcessingLevel, response);
              }
            }),
            take(1)
          );
        } else {
          return of(null);
        }
      })
    );
  }

  /**
   * Process rate response as they come in
   * @param currentProcessingLevel {eProductLevel}
   * @param rateResponse {IRateResponse}
   */
  processRateResponse(
    currentProcessingLevel: eProductLevel,
    rateResponse: IRateResponse
  ): void {
    this.penTrackKeys[currentProcessingLevel] = rateResponse.penTrackKey;
    if (currentProcessingLevel === eProductLevel.mmp && rateResponse) {
      this.mmpExclusionText = rateResponse.mmpExclusionText;
    }
    if (
      rateResponse?.paymentProtectionRates?.length &&
      this.udStateService.userAccount['isEnablePaymentProtectionAgeRule']
    ) {
      // Cache the full rate list so eligibility can be re-evaluated when ages
      // change without requiring another rate call.
      this.udStateService.setFullPaymentProtectionRates([
        ...rateResponse.paymentProtectionRates
      ]);
      const { rates: filteredPpiRates, eligibility, isPartialDueToIneligibleCoApplicant } =
        filterPpiRatesByAge(
          rateResponse.paymentProtectionRates,
          this.udStateService.getBorrowerCoBorrowerAge()
        );
      rateResponse.paymentProtectionRates = filteredPpiRates;
      if (eligibility === PPIAgeEligibility.NoneEligible) {
        this.notificationService.showError(PPI_INELIGIBLE_AGE_RULE_MESSAGE);
      }
      this.udStateService.noPPIRateAfterAgeFilter.next(
        eligibility === PPIAgeEligibility.NoneEligible
      );
      this.udStateService.partialPPIRateAfterAgeFilter.next(
        isPartialDueToIneligibleCoApplicant
      );
      // Auto-select PP (new quote, both eligible).
      this.udStateService.evaluatePpiAutoSelect();
    }
    if (
      currentProcessingLevel === eProductLevel.ppm &&
      rateResponse?.ppmRates?.length
    ) {
      const isMMPSelected: boolean = !!this.udStateService
        .getCurrentSelectedProducts()
        .includes(eProductNames.MMP);
      if (isMMPSelected) {
        rateResponse.ppmRates = rateResponse.ppmRates.filter(
          (ppmRate: PpmRate) => !ppmRate.ineligibleIfMMPSelected
        );
      }
    }
    this.udStateService.setProductsRatesResponse(rateResponse);
    if (currentProcessingLevel !== eProductLevel.ppi) {
      this.calculateTAF(null, rateResponse);
    }
  }

  /**
   * Method to calculate initial TAF before rates call
   * @param skipProductsRerate {eProductNames[]} as an array
   */
  calculateInitialTAF(skipProductsRerate?: eProductNames[]): void {
    let { amountFinanced, otherFees, deductions } =
      this.udStateService.getRateRequestValues().loanData;
    amountFinanced = amountFinanced ?? 0;
    otherFees = otherFees ?? 0;
    deductions = deductions ?? 0;
    if (skipProductsRerate?.length) {
      this.totalAmountFinanced = new Big(amountFinanced)
        .plus(otherFees)
        .minus(deductions);
      skipProductsRerate.forEach((prod: eProductNames) => {
        switch (prod) {
          case eProductNames.MMP: {
            this.calculateMmpTAF();
            break;
          }
          case eProductNames.PPM: {
            this.calculatePpmTAF();
            break;
          }
          case eProductNames.KP: {
            this.calculateKpTAF();
            break;
          }
          case eProductNames.VR: {
            this.calculateVrTAF();
            break;
          }
          case eProductNames.GAP: {
            this.calculateGapTAF();
            break;
          }
          case eProductNames.SD: {
            this.calculateSdTAF();
            break;
          }
          case eProductNames.PEP: {
            this.calculatePepTAF();
            break;
          }
          case eProductNames.CVP: {
            this.calculateCvpTAF();
            break;
          }
        }
      });
    } else {
      this.totalAmountFinanced = new Big(amountFinanced)
        .plus(otherFees)
        .minus(deductions);
    }
  }

  /**
   * Calculate TAF with MMP
   */
  calculateMmpTAF(): void {
    const selectedMmpRate: MmpRate = this.udStateService.getProdSelectedRateMap(
      eProductNames.MMP
    ) as MmpRate;
    if (selectedMmpRate) {
      if (
        this.udStateService.getProductFormValues(eProductNames.MMP).isDiscounted
      ) {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          selectedMmpRate?.planCost_Discounted
        );
      } else {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          selectedMmpRate?.planCost
        );
      }
    }
    if (selectedMmpRate?.planOptions?.length) {
      const selectedOptions = selectedMmpRate.planOptions.filter(
        (planOption: PlanOption) => planOption.selected
      );
      selectedOptions.forEach(
        (planOption: PlanOption) =>
        (this.totalAmountFinanced = this.totalAmountFinanced.plus(
          planOption?.totalOptionCostAmount
        ))
      );
      // Include TLR cost when it's on the loan but not yet auto-selected, so
      // GAP/PPI rate off the correct TAF. isTlrOnLoan() excludes a TLR the user
      // declined, avoiding a /Book mismatch equal to the TLR cost.
      if (this.isTlrOnLoan()) {
        const tlrOption = selectedMmpRate.planOptions.find(
          (opt: PlanOption) => opt.isTotalLossRefund
        );
        if (tlrOption && !tlrOption.selected) {
          this.totalAmountFinanced = this.totalAmountFinanced.plus(
            tlrOption?.totalOptionCostAmount
          );
        }
      }
    }
  }

  /**
   * Calculate TAF with PPM
   */
  calculatePpmTAF(): void {
    const selectedPpmRate: PpmRate = this.udStateService.getProdSelectedRateMap(
      eProductNames.PPM
    ) as PpmRate;
    if (selectedPpmRate) {
      this.totalAmountFinanced = this.totalAmountFinanced.plus(
        selectedPpmRate?.totalPremium
      );
    }
  }

  /**
   * Calculate TAF with KP
   */
  calculateKpTAF(): void {
    const selectedKeyProtectionRate: KeyProtectionRate =
      this.udStateService.getProdSelectedRateMap(
        eProductNames.KP
      ) as KeyProtectionRate;
    if (selectedKeyProtectionRate) {
      this.totalAmountFinanced = this.totalAmountFinanced.plus(
        selectedKeyProtectionRate?.totalPremium
      );
    }
  }

  /**
   * Calculate TAF with VR
   */
  calculateVrTAF(): void {
    const selectedVrRate: VehicleReturnRate =
      this.udStateService.getProdSelectedRateMap(
        eProductNames.VR
      ) as VehicleReturnRate;
    if (selectedVrRate) {
      this.totalAmountFinanced = this.totalAmountFinanced.plus(
        selectedVrRate?.totalPremium
      );
    }
  }

  /**
   * Calculate TAF with GAP
   */
  calculateGapTAF(): void {
    const selectedGapRate: GapRate = this.udStateService.getProdSelectedRateMap(
      eProductNames.GAP
    ) as GapRate;
    if (selectedGapRate) {
      if (
        this.udStateService.getProductFormValues(eProductNames.GAP).isDiscounted
      ) {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          selectedGapRate?.discountedPremium
        );
      } else {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          selectedGapRate?.totalPremium
        );
      }
    }
  }

  /**
   * Calculate TAF with SD
   */
  calculateSdTAF(): void {
    const selectedSdRate: SdRate = this.udStateService.getProdSelectedRateMap(
      eProductNames.SD
    ) as SdRate;
    if (selectedSdRate) {
      this.totalAmountFinanced = this.totalAmountFinanced.plus(
        selectedSdRate?.totalPremium
      );
    }
  }

  /**
   * Calculate TAF with PEP
   */
  calculatePepTAF(): void {
    const selectedPepRate: PepRate = this.udStateService.getProdSelectedRateMap(
      eProductNames.PEP
    ) as PepRate;
    if (selectedPepRate) {
      if (
        this.udStateService.getProductFormValues(eProductNames.PEP).isDiscounted
      ) {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          selectedPepRate?.discountedPremium
        );
      } else {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          selectedPepRate?.totalPremium
        );
      }
    }
  }

  /**
   * Calculate TAF with CVP
   */
  calculateCvpTAF(): void {
    const selectedCvpRate: CvpRate = this.udStateService.getProdSelectedRateMap(
      eProductNames.CVP
    ) as CvpRate;
    if (selectedCvpRate) {
      this.totalAmountFinanced = this.totalAmountFinanced.plus(
        selectedCvpRate?.totalPremium
      );
    }
  }

  /**
   * Calculate TAF as the response for each rate call is received. Save TAF in state.
   * @param isExistingProposalRates {boolean} for proposals returning to worksheet
   * @param currentRateResponse {IRateResponse} defaults to ratesresponse from state if not passed
   */
  calculateTAF(
    isExistingProposalRates?: boolean,
    currentRateResponse: IRateResponse = this.udStateService.getProductsRatesResponse()
  ): void {
    let { amountFinanced, otherFees, deductions } =
      this.udStateService.getRateRequestValues().loanData;
    amountFinanced = amountFinanced ?? 0;
    otherFees = otherFees ?? 0;
    deductions = deductions ?? 0;
    if (isExistingProposalRates) {
      this.totalAmountFinanced = new Big(amountFinanced)
        .plus(otherFees)
        .minus(deductions);
    }
    const mmpRequest = this.udStateService.getProductFormValues(
      eProductNames.MMP
    );
    const ppmRequest = this.udStateService.getProductFormValues(
      eProductNames.PPM
    );
    const keyProtectionRequest = this.udStateService.getProductFormValues(
      eProductNames.KP
    );
    const gapRequest = this.udStateService.getProductFormValues(
      eProductNames.GAP
    );
    const pepRequest = this.udStateService.getProductFormValues(
      eProductNames.PEP
    );
    const sdRequest = this.udStateService.getProductFormValues(
      eProductNames.SD
    );
    const vehicleReturnRequest = this.udStateService.getProductFormValues(
      eProductNames.VR
    );
    const cvpRequest = this.udStateService.getProductFormValues(
      eProductNames.CVP
    );

    for (const product in eProductNames) {
      const productRate =
        currentRateResponse?.[eProductNames[product] + 'Rates'];
      if (productRate?.length) {
        switch (eProductNames[product]) {
          case eProductNames.MMP:
            this.calculateMmpFinalTAF(mmpRequest, currentRateResponse);
            break;
          case eProductNames.PPM:
            this.calculatePpmFinalTAF(ppmRequest, currentRateResponse);
            break;
          case eProductNames.KP:
            this.calculateKpFinalTAF(keyProtectionRequest, currentRateResponse);
            break;
          case eProductNames.VR:
            this.calculateVrFinalTAF(vehicleReturnRequest, currentRateResponse);
            break;
          case eProductNames.GAP:
            this.calculateGapFinalTAF(gapRequest, currentRateResponse);
            break;
          case eProductNames.SD:
            this.calculateSdFinalTAF(sdRequest, currentRateResponse);
            break;
          case eProductNames.PEP:
            this.calculatePepFinalTAF(pepRequest, currentRateResponse);
            break;
          case eProductNames.CVP:
            this.calculateCvpFinalTAF(cvpRequest, currentRateResponse);
            break;
        }
      }
    }
    this.udStateService.totalAmountFinanced =
      this.totalAmountFinanced.toNumber();
  }

  /**
   * Calculate TAF for MMP
   * @param mmpRequest {any}
   * @param currentRateResponse {IRateResponse}
   */
  calculateMmpFinalTAF(
    mmpRequest: any,
    currentRateResponse: IRateResponse
  ): void {
    if (currentRateResponse?.mmpRates) {
      const mmpRateIndex = this.getRateIndex(
        mmpRequest.selectedMmpRate,
        currentRateResponse.mmpRates
      );
      const additionalOptionsSelected: string[] =
        this.udStateService.getMmpPlanOptionsSelected();
      if (!mmpRequest?.isDiscounted) {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          currentRateResponse.mmpRates[mmpRateIndex].planCost
        );
      } else {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          currentRateResponse.mmpRates[mmpRateIndex].planCost_Discounted
        );
      }
      if (additionalOptionsSelected?.length) {
        const mmpRate = currentRateResponse.mmpRates[mmpRateIndex];
        mmpRate?.planOptions?.forEach((planOption: PlanOption) => {
          if (additionalOptionsSelected.includes(planOption.id)) {
            this.totalAmountFinanced = this.totalAmountFinanced.plus(
              planOption.totalOptionCostAmount
            );
          }
        });
      }
      // Include TLR cost when it's on the loan but not yet in the selected
      // options, so GAP/PPI rate off the correct TAF. isTlrOnLoan() excludes a
      // TLR the user declined, avoiding a /Book mismatch equal to the TLR cost.
      if (this.isTlrOnLoan()) {
        const mmpRate = currentRateResponse.mmpRates[mmpRateIndex];
        const tlrOption = mmpRate?.planOptions?.find(
          (opt: PlanOption) => opt.isTotalLossRefund
        );
        if (
          tlrOption &&
          !additionalOptionsSelected?.includes(tlrOption.id)
        ) {
          this.totalAmountFinanced = this.totalAmountFinanced.plus(
            tlrOption.totalOptionCostAmount
          );
        }
      }
    }
  }

  /**
   * Calculate TAF for PPM
   * @param ppmRequest {any}
   * @param currentRateResponse {IRateResponse}
   */
  calculatePpmFinalTAF(
    ppmRequest: any,
    currentRateResponse: IRateResponse
  ): void {
    if (currentRateResponse?.ppmRates) {
      const ppmRateIndex = this.getRateIndex(
        ppmRequest.selectedPpmRate,
        currentRateResponse.ppmRates
      );
      this.totalAmountFinanced = this.totalAmountFinanced.plus(
        currentRateResponse.ppmRates[ppmRateIndex].totalPremium
      );
    }
  }

  /**
   * Calculate TAF for SD
   * @param sdRequest {any}
   * @param currentRateResponse {IRateResponse}
   */
  calculateSdFinalTAF(
    sdRequest: any,
    currentRateResponse: IRateResponse
  ): void {
    if (currentRateResponse?.sdRates) {
      const sdRateIndex = this.getRateIndex(
        sdRequest.selectedSdRate,
        currentRateResponse.sdRates
      );
      if (currentRateResponse?.sdRates[sdRateIndex]?.totalPremium) {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          currentRateResponse.sdRates[sdRateIndex].totalPremium
        );
      }
    }
  }

  /**
   * Calculate TAF for KP
   * @param keyProtectionRequest {any}
   * @param currentRateResponse {IRateResponse}
   */
  calculateKpFinalTAF(
    keyProtectionRequest: any,
    currentRateResponse: IRateResponse
  ): void {
    if (currentRateResponse?.keyProtectionRates) {
      const kpRateIndex = this.getRateIndex(
        keyProtectionRequest.selectedKeyProtectionRate,
        currentRateResponse.keyProtectionRates
      );
      this.totalAmountFinanced = this.totalAmountFinanced.plus(
        currentRateResponse.keyProtectionRates[kpRateIndex].totalPremium
      );
    }
  }

  /**
   * Calculate TAF for CVP
   * @param cvpRequest {any}
   * @param currentRateResponse {IRateResponse}
   */
  calculateCvpFinalTAF(
    cvpRequest: any,
    currentRateResponse: IRateResponse
  ): void {
    if (currentRateResponse?.cvpRates?.length) {
      const cvpRateIndex = this.getRateIndex(
        cvpRequest.selectedCvpRate,
        currentRateResponse.cvpRates
      );
      if (cvpRateIndex >= 0) {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          currentRateResponse.cvpRates[cvpRateIndex].totalPremium
        );
      }
    }
  }

  /**
   * Calculate TAF for VR
   * @param vehicleReturnRequest {any}
   * @param currentRateResponse {IRateResponse}
   */
  calculateVrFinalTAF(
    vehicleReturnRequest: any,
    currentRateResponse: IRateResponse
  ): void {
    if (currentRateResponse?.vehicleReturnRates) {
      const vrRateIndex = this.getRateIndex(
        vehicleReturnRequest.selectedVrRate,
        currentRateResponse.vehicleReturnRates
      );
      this.totalAmountFinanced = this.totalAmountFinanced.plus(
        currentRateResponse.vehicleReturnRates[vrRateIndex].totalPremium
      );
    }
  }

  /**
   * Calculate TAF for Gap
   * @param gapRequest {any}
   * @param currentRateResponse {IRateResponse}
   */
  calculateGapFinalTAF(
    gapRequest: any,
    currentRateResponse: IRateResponse
  ): void {
    if (currentRateResponse?.gapRates) {
      const gapRateIndex = this.getRateIndex(
        gapRequest?.selectedGapRate,
        currentRateResponse?.gapRates
      );
      if (!gapRequest?.isDiscounted) {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          currentRateResponse.gapRates[gapRateIndex].totalPremium
        );
      } else {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          currentRateResponse.gapRates[gapRateIndex].discountedPremium
        );
      }
    }
  }

  /**
   * Calculates TAF for PEP
   * @param pepRequest {any}
   * @param currentRateResponse {IRateResponse}
   */
  calculatePepFinalTAF(
    pepRequest: any,
    currentRateResponse: IRateResponse
  ): void {
    if (currentRateResponse?.pepRates) {
      const pepRateIndex = this.getRateIndex(
        pepRequest.selectedPepRate,
        currentRateResponse.pepRates
      );
      if (!pepRequest?.isDiscounted) {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          currentRateResponse.pepRates[pepRateIndex].totalPremium
        );
      } else {
        this.totalAmountFinanced = this.totalAmountFinanced.plus(
          currentRateResponse.pepRates[pepRateIndex].discountedPremium
        );
      }
    }
  }

  /**
   * Get the index of the selected rateId
   * @param selectedProdRate of type {rateId: number, rateCollectionId: number}
   * @param prodRates of type UProductRates
   * @returns number - matching index in the array
   */
  getRateIndex(
    selectedProdRate: { rateId: number; rateCollectionId: number },
    prodRates: UProductRates
  ): number {
    const prodRateId = selectedProdRate?.rateId;
    let prodRateIndex: number = 0;
    if (prodRateId) {
      prodRateIndex = prodRates.findIndex(
        (rate: UProductRate) => rate.rateId === prodRateId
      );
    }
    return prodRateIndex === -1 ? 0 : prodRateIndex;
  }

  /**
   * Get rates for products whether on initial load or during changes to products
   * @param prodPyramid - indicates the level in the productPyramid
   * @param level - indicates the level in the productPyramid
   * @param rateRequest - indicates the level in the productPyramid
   * @returns rate response as an observable
   */
  filterProdRatesByLevel(
    prodPyramidLevel: any,
    rateRequest: IRateRequest
  ): IRateRequest {
    prodPyramidLevel = [...new Set(prodPyramidLevel)];
    prodPyramidLevel?.forEach((product: eProductNames) => {
      const currentSelectedProducts =
        this.udStateService.getCurrentSelectedProducts();
      rateRequest[product] = this.udStateService.getProductFormValues(product);
      if (
        !currentSelectedProducts.includes(product) ||
        this.udStateService.getUserDisabledProducts().includes(product)
      ) {
        if (rateRequest[product]) {
          rateRequest[product]['isExcluded'] = true;
        }
      } else if (rateRequest[product]) {
        rateRequest[product]['isExcluded'] = false;
      }
    });
    return rateRequest;
  }

  /**
   * If LtV is not set and the app is UD, we need to show an error to tell the user
   * to look in the vehicle info section
   * @returns bool for if the error should be shown
   */
  isLtVSet(): boolean {
    return (
      !this.isLoanToValueSet &&
      sessionStorage.getItem('appName') === AppNames.UNITY_DIRECT
    );
  }

  /**
   * We need to know if the loan date needs corrected in the loanData
   * @param loanDate - loanData loanDate
   * @returns bool to show if the coverage date and loan date do not match.
   */
  doesCoverageDateMatchLoanDate(loanDate: any): boolean {
    return (
      this.udStateService.getCoverageDate() &&
      loanDate !== this.udStateService.getCoverageDate()
    );
  }

  /**
   * Checks to see if the mmp data in the rate req is missing elig fields
   * @param mmp - mmp data in ratesRequest being processed
   * @returns bool to represent if eligible data is missing in the mmp info
   */
  isMMPEligFieldMissing(mmp: any): boolean {
    return !mmp?.eligMilesPerYear || !mmp?.eligYearsOwnVehicle;
  }

  /**
   * Checks to see if TLR is required on the account
   * @returns bool to show if TLR option is required
   */
  isTlrRequired(): boolean {
    return (
      this.udStateService.userAccount.totalLossRefundRequired ||
      this.udStateService.userAccount.preSelectTotalLossRefund
    );
  }

  /**
   * Whether the TLR cost belongs in the TAF. Unlike isTlrRequired(), this
   * honors a user declining a pre-selected TLR (required TLR is always on),
   * preventing a /Book mismatch equal to the TLR cost.
   */
  isTlrOnLoan(): boolean {
    if (this.udStateService.userAccount.totalLossRefundRequired) {
      return true;
    }
    return (
      this.udStateService.userAccount.preSelectTotalLossRefund &&
      !this.udStateService.getTlrDeclined()
    );
  }

  /**
   * Derives tlrDeclined from the saved selection when an existing proposal is
   * restored (Review -> back -> Quote), where the UI never runs mmpCoverChange.
   * Only for preSelectTotalLossRefund accounts: a TLR option absent from the
   * saved selection means the user declined it. Call before calculateTAF().
   */
  syncTlrDeclinedFromSelection(
    currentRateResponse: IRateResponse = this.udStateService.getProductsRatesResponse() as IRateResponse
  ): void {
    const account = this.udStateService.userAccount;
    // Required TLR can't be declined; nothing to derive for non-preselect.
    if (account.totalLossRefundRequired || !account.preSelectTotalLossRefund) {
      return;
    }
    // Existing proposals only: on a new one the TLR auto-select hasn't fired,
    // so absence from the selection doesn't mean the user declined it.
    if (!this.udStateService.proposalId) {
      return;
    }
    const mmpRates = currentRateResponse?.mmpRates;
    if (!mmpRates?.length) {
      return;
    }
    const tlrOption = mmpRates
      .flatMap((rate: MmpRate) => rate?.planOptions || [])
      .find((opt: PlanOption) => opt?.isTotalLossRefund);
    if (!tlrOption) {
      return;
    }
    const selectedOptions = this.udStateService.getMmpPlanOptionsSelected();
    this.udStateService.setTlrDeclined(!selectedOptions.includes(tlrOption.id));
  }

  /**
   * Get rates for products whether on initial load or during changes to products
   * @param level - indicates the level in the productPyramid
   * @returns rate response as an observable
   */
  getProductRates(level: string): Observable<IRateResponse> {
    this.logger.info('Getting rates for LEVEL: ', level);
    const userAccount = this.udStateService.userAccount;

    let rateRequest: IRateRequest = {
      ...this.udStateService.getRateRequestValues()
    };
    rateRequest.portalAccountId = userAccount.portalAccountId;
    rateRequest.clientApplicationId =
      Constants.appConfiguration.clientApplicationId;
    rateRequest.loanData.totalAmountFinanced =
      this.totalAmountFinanced.toNumber() || 0;
    if (
      level === eProductLevel.ppi &&
      rateRequest?.vehicle?.vin &&
      (this.udStateService.valuationDialogClicked ||
        sessionStorage.getItem('appName') === AppNames.UNITY_EXPRESS)
    ) {
      this.udStateService.loanValue = loanToValue(
        rateRequest.loanData.totalAmountFinanced,
        this.udStateService.vehicleValue
      );
      if (this.isLtVSet()) {
        this.notificationService.showError(
          'Loan to Value estimate is shown in the Vehicle Info section: ' +
          this.udStateService.loanValue +
          '%'
        );
      }
      this.isLoanToValueSet = true;
    }
    if (!rateRequest.vehicle.purchasePrice) {
      rateRequest.vehicle.purchasePrice = 0;
    }
    if (!rateRequest.vehicle?.retailPrice) {
      rateRequest.vehicle.retailPrice = 0;
    }
    if (this.penTrackKeys?.[level]) {
      rateRequest.penTrack = { penTrackKey: this.penTrackKeys[level] };
    }
    if (level === eProductLevel.gap) {
      // This is required for when options are added to an existing proposal
      rateRequest.loanData.totalAmountFinanced =
        this.totalAmountFinanced.toNumber();
    }
    this.initialCallLogic(rateRequest);
    return this.ratesRequestProcessAndSubmit(rateRequest, level);
  }

  /**
   * Finish preparing the rateRequest before sending it to /Rates
   * @param rateRequest - rateRequest to process before sending
   * @param level - indicates the level in the productPyramid
   * @returns rate response as an observable
   */
  ratesRequestProcessAndSubmit(
    rateRequest: IRateRequest,
    level: string
  ): Observable<IRateResponse> {
    if (rateRequest.vehicle?.vin === '' || rateRequest.vehicle?.vin === null) {
      this.mockVin = this.mockVin || this.udStateService.mockVin;
      this.mockVin = this.mockVin?.replaceAll('"', '');
      rateRequest.vehicle.mockVin = this.mockVin;
    } else {
      rateRequest.vehicle.mockVin = null;
    }
    const prodPyramid = this.udStateService.getProductPyramid();
    this.logger.info('Product Pyramid >>> ', prodPyramid);
    if (prodPyramid?.[level]?.length) {
      rateRequest = this.filterProdRatesByLevel(
        prodPyramid?.[level],
        rateRequest
      );
      if (
        prodPyramid?.[level]?.length &&
        prodPyramid?.[level].every(
          (product: eProductNames) =>
            (product !== eProductNames.MMP &&
              !rateRequest[product]?.isSelected) ||
            (product === eProductNames.MMP && rateRequest[product]?.isExcluded)
        )
      ) {
        // If for a given level, none of the products are selected, then stop the Rates call as BE can return an error
        return of(<IRateResponse>{});
      }
    }
    if (!this.udStateService.isAppDirect) {
      const hubData = this.hubDataService.getProposalFromHubData();
      if (
        rateRequest.paymentProtection &&
        hubData.losInterfaceLink.combinationId
      ) {
        rateRequest.paymentProtection.combinationId = String(
          hubData.losInterfaceLink.combinationId
        );
      }
    }
    // To update loan date when coverage date changes
    if (this.doesCoverageDateMatchLoanDate(rateRequest.loanData.loanDate)) {
      rateRequest.loanData.loanDate = this.udStateService.getCoverageDate();
      rateRequest.loanData.coverageDate = this.udStateService.getCoverageDate();
    }
    if (!rateRequest.mmp?.isSelected) {
      delete rateRequest.mmp;
    } else {
      if (this.isMMPEligFieldMissing(rateRequest.mmp)) {
        rateRequest.mmp.eligMilesPerYear =
          rateRequest.mmp.eligMilesPerYear || MMPDefaults.Miles;
        rateRequest.mmp.eligYearsOwnVehicle =
          rateRequest.mmp.eligYearsOwnVehicle || MMPDefaults.Years;
      }
    }
    return this.http
      .post<IRateResponse>(
        `${Constants.appConfiguration.apiRoot}/Rates`,
        rateRequest
      )
      .pipe(
        catchError((err: string) => {
          return this.handleRateError(err);
        })
      );
  }

  /**
   * Calculate total of MMP options for GAP request
   * @returns number
   */
  calculateMmpOptionsCost(): number {
    const prodPyramid = this.udStateService.getProductPyramid();
    if (!prodPyramid[eProductLevel.mmp]) {
      return 0;
    }
    const optionsSelected = this.udStateService.getMmpPlanOptionsSelected();
    let total: Big = Big(0);
    optionsSelected?.forEach((optId: string) => {
      const selectedPlan = (
        this.udStateService.getSelectedMMPRate() as MmpRate
      )?.planOptions.find((plOpt: PlanOption) => plOpt.id === optId);
      if (
        selectedPlan &&
        ((selectedPlan.isTotalLossRefund &&
          !this.udStateService.userAccount.preSelectTotalLossRefund) ||
          !selectedPlan.isTotalLossRefund)
      ) {
        selectedPlan.selected = true;
        total = total.plus(selectedPlan?.totalOptionCostAmount || 0);
      }
    });
    return total.toNumber();
  }

  // Handles Rate error for 503
  handleRateError(err: string): Observable<IRateResponse> {
    // We get the err in string format from Error Interceptor and it contains the status code
    const errIgnore = isIgnoreError(err);
    if (errIgnore) {
      return of(<IRateResponse>{});
    }
  }
  /**
   * Logic and Key deletion for intial rate calls as BE expects a value from these fields.
   * @param rateRequest of type IRateRequest
   */
  initialCallLogic(rateRequest: IRateRequest): void {
    const currentSelectedProducts =
      this.udStateService.getCurrentSelectedProducts();
    if (currentSelectedProducts.indexOf(eProductNames.MMP) !== -1) {
      // If MMP is enabled, coverage date is loan date
      rateRequest.loanData.coverageDate = rateRequest.loanData.loanDate;
    } else {
      delete rateRequest.loanData.coverageDate;
    }
    if (rateRequest.loanData.hasOwnProperty('totalMonths')) {
      rateRequest.loanData.termInMonths = rateRequest.loanData['totalMonths'];
    }
    if (!rateRequest.vehicle.vin) {
      rateRequest.vehicle.isVehicleManuallySelected = true;
    } else {
      rateRequest.vehicle.isVehicleManuallySelected = false;
    }
    if (rateRequest.vehicle.hasLiftKitOrOversizeTires === null) {
      delete rateRequest.vehicle.hasLiftKitOrOversizeTires;
    }
    if (rateRequest.vehicle.isModified === null) {
      delete rateRequest.vehicle.isModified;
    }
    if (rateRequest.vehicle.certifiedPreowned === null) {
      delete rateRequest.vehicle.certifiedPreowned;
    }
    if (rateRequest.vehicle.secondOwner === null) {
      delete rateRequest.vehicle.secondOwner;
    }
    if (rateRequest.vehicle.isVehicleManuallySelected === null) {
      delete rateRequest.vehicle.isVehicleManuallySelected;
    }
    if (
      rateRequest.vehicle?.vehicleExceptionAndOption?.optionFourWheelDrive ===
      null
    ) {
      delete rateRequest.vehicle?.vehicleExceptionAndOption;
    }
    if (rateRequest.penTrack?.penTrackKey === null) {
      delete rateRequest.penTrack.penTrackKey; //Needed. Check rates
    }
    if (rateRequest.loanData.payment === null) {
      delete rateRequest.loanData.payment; // When the calculate button is clicked we get the payment number from BE.
    }
    if (rateRequest.loanData.calculateByTerm === null) {
      delete rateRequest.loanData.calculateByTerm;
    }
  }

  /**
   * Check if rerating is required for existing proposal
   * @returns boolean
   */
  isReratingRequired(loanData: ILoan): Observable<boolean> {
    // Clones as new obj to avoid state modifications with the changes to rateRequest below
    const rateRequest: IRateRequest = {
      ...this.udStateService.getRateRequestValues()
    };
    this.adjustProductsInRateRequest(loanData, rateRequest);
    if (
      (this.udStateService.isNewProposal && !this.udStateService.proposalId) ||
      (loanData?.vehicle.isCommercial !== rateRequest.vehicle.isCommercial &&
        !rateRequest.vehicle.collateralType &&
        !this.udStateService.isNewProposal &&
        this.udStateService.userAccount?.isRequireCommercial)
    ) {
      return of(true);
    } else {
      const vehicle = this.udStateService.isVehicle;
      const proposalId: string = this.udStateService.proposalId;
      if (vehicle) {
        rateRequest.loanData.loanType = LoanType.Vehicle;
      } else {
        rateRequest.loanData.loanType = LoanType.OtherCollateral;
      }
      if (this.udStateService.isAppDirect) {
        delete rateRequest['losId'];
        delete rateRequest['losInterfaceLink'];
      } else {
        rateRequest['losInterfaceLink'] = loanData.losInterfaceLink;
        rateRequest['losId'] = loanData.losInterfaceLink.losId;
      }
      this.removeUnwantedPropsFromRequest(rateRequest);

      if (!rateRequest.mmp?.isSelected) {
        delete rateRequest.mmp;
      }
      return this.http.post<boolean>(
        `${Constants.appConfiguration.apiRoot}/Proposals/${proposalId}/ReRating`,
        rateRequest
      );
    }
  }

  /**
   * Adjusts Product information in the rateRequest.
   * @param loanData {ILoan}
   * @param rateRequest {IRateRequest}
   */
  adjustProductsInRateRequest(
    loanData: ILoan,
    rateRequest: IRateRequest
  ): void {
    for (const prod in eProductNames) {
      const isProdSelected: boolean =
        loanData[eProductNames[prod]]?.['selected'] ||
        loanData[eProductNames[prod]]?.['isSelected'] ||
        false; // Avoid setting null
      if (eProductNames[prod] !== eProductNames.MMP) {
        const defaultSelectionObj = getDefaultProdSelectionObject();
        rateRequest[eProductNames[prod]] = defaultSelectionObj;
        rateRequest[eProductNames[prod]].isSelected = isProdSelected;
      } else {
        const mmpSelectionObj = getNewMmpRequest();
        rateRequest[eProductNames[prod]] = mmpSelectionObj;
        rateRequest[eProductNames[prod]].isExcluded = !isProdSelected;
        rateRequest[eProductNames[prod]].eligMilesPerYear = isProdSelected
          ? (loanData.mmp?.eligMilesPerYear || MMPDefaults.Miles)
          : null;
        rateRequest[eProductNames[prod]].eligYearsOwnVehicle = isProdSelected
          ? (loanData.mmp?.eligYearsOwnVehicle || MMPDefaults.Years)
          : null;
        rateRequest[eProductNames[prod]].isSelected = isProdSelected;
      }
    }
  }

  /**
   * Removes unwanted properties from request.
   * @param rateRequest {IRateRequest}
   */
  removeUnwantedPropsFromRequest(rateRequest: IRateRequest): void {
    delete rateRequest['id'];
    delete rateRequest['borrower'];
    delete rateRequest['message'];
    if (rateRequest.vehicle.hasLiftKitOrOversizeTires === null) {
      delete rateRequest.vehicle.hasLiftKitOrOversizeTires;
    }
    if (rateRequest.vehicle.isModified === null) {
      delete rateRequest.vehicle.isModified;
    }
    if (rateRequest.vehicle.certifiedPreowned === null) {
      delete rateRequest.vehicle.certifiedPreowned;
    }
    if (rateRequest.vehicle.secondOwner === null) {
      delete rateRequest.vehicle.secondOwner;
    }
    if (rateRequest.vehicle.isVehicleManuallySelected === null) {
      delete rateRequest.vehicle.isVehicleManuallySelected;
    }
    if (
      rateRequest.vehicle?.vehicleExceptionAndOption?.optionFourWheelDrive ===
      null
    ) {
      delete rateRequest.vehicle?.vehicleExceptionAndOption;
    }
    if (rateRequest.penTrack?.penTrackKey === null) {
      delete rateRequest.penTrack.penTrackKey; //Needed. Check rates
    }
    if (rateRequest.loanData.payment === null) {
      delete rateRequest.loanData.payment; // When the calculate button is clicked we get the payment number from BE.
    }
    if (rateRequest.loanData.calculateByTerm === null) {
      delete rateRequest.loanData.calculateByTerm;
    }
    if (rateRequest.vehicle.collateralType === 0) {
      rateRequest.loanData.loanType = LoanType.Vehicle;
    } else {
      rateRequest.loanData.loanType = LoanType.OtherCollateral;
    }
  }

  /**
   * Get rates of existing proposal
   * @returns IRateResponse
   */
  getExistingProposalRates(
    proposalId: string
  ): Observable<IExistingRatesResponse> {
    return this.http.get<IExistingRatesResponse>(
      `${Constants.appConfiguration.apiRoot}/Proposals/${proposalId}/Rates`
    );
  }

  /**
   * Gets the rateCollections for products in existing proposal scenario
   * @param rates {IExistingRatesResponse}
   * @returns {Observable<IRateResponse>}
   */
  fetchRateCollections(
    rates: IExistingRatesResponse
  ): Observable<IRateResponse> {
    const rateCollectionReqObj = this.createRateCollectionRequestObj(rates);
    rateCollectionReqObj.portalAccountId = Number(
      this.udStateService.userInfo.AccountId
    );
    return this.http.post<IRateResponse>(
      `${Constants.appConfiguration.apiRoot}/Rates/RateCollections`,
      rateCollectionReqObj
    );
  }

  /**
   * Returns index of selected rateId in provided rates array
   * @param selectedRateId {number}
   * @param prodRates {UProductRates}
   * @returns {number}
   */
  getProdRateIndex(selectedRateId: number, prodRates: UProductRates): number {
    const idx = prodRates?.findIndex(
      (x: UProductRate) => x.rateId === selectedRateId
    );
    return idx === -1 ? 0 : idx;
  }

  /**
   * Given Proposal rates, method returns rateCollectionRequest
   * @param rates {IExistingRatesResponse}
   * @returns {RateCollectionRequest}
   */
  createRateCollectionRequestObj(
    rates: IExistingRatesResponse
  ): RateCollectionRequest {
    const rateCollectionReqObj = getEmptyRateCollectionRequest();
    for (const product in eProductNames) {
      const prodSelectionRateCollectionIdObj =
        getEmptyProductSelectionRateCollectionId();
      const productRate = rates?.[eProductNames[product] + 'Rates'];
      const propKey =
        product === 'PPI'
          ? 'selectedPpiRateId'
          : `selected${eProductRateNames[eProductNames[product]]}RateId`;
      const prodRateIndex = this.getProdRateIndex(rates[propKey], productRate);
      if (productRate?.length) {
        rateCollectionReqObj[eProductNames[product]] = {
          ...prodSelectionRateCollectionIdObj
        };
        rateCollectionReqObj[eProductNames[product]].isSelected = true;
        if (product === eProductNames.MMP) {
          rateCollectionReqObj[eProductNames.MMP].isExcluded = false;
        }
        rateCollectionReqObj[eProductNames[product]].rateCollectionId =
          productRate[prodRateIndex].rateCollectionId;
      } else {
        delete rateCollectionReqObj[eProductNames[product]];
      }
    }
    rateCollectionReqObj.portalAccountId = Number(
      this.udStateService.userInfo.AccountId
    );
    return rateCollectionReqObj;
  }
}
